import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'

import type { DisciplineVerzameling } from '@models/DisciplineVerzameling'

import { DisciplineIconFlagBadge } from './DisciplineIconFlagBadge'

export { DisciplineIcons }

const backgroundDict = {
  /** Electriciteit */
  E: {
    backgroundColor: 'rgb(79, 186, 219)',
    backgroundImage: 'url(/images/elektriciteit-icon-white.svg)',
  } /** Gas */,
  G: {
    backgroundColor: 'rgb(188, 53, 141)',
    backgroundImage: 'url(/images/gas-icon-white.svg)',
  } /** Water */,
  W: {
    backgroundColor: 'rgb(61, 108, 180)',
    backgroundImage: 'url(/images/water-icon-white.svg)',
  } /** Media */,
  M: {
    backgroundColor: 'rgb(40, 162, 124)',
    backgroundImage: 'url(/images/media-icon-white.svg)',
  } /** CAI */,
  C: {
    backgroundColor: 'rgb(231, 0, 0)',
    backgroundImage: 'url(/images/media-icon-white.svg)',
  } /** Koper */,
  K: {
    backgroundColor: 'rgb(228, 193, 24)',
    backgroundImage: 'url(/images/media-icon-white.svg)',
  } /** Heat (warmte, W was bezet voor water) */,
  H: {
    backgroundColor: 'rgb(221, 128, 37)',
    backgroundImage: 'url(/images/heat-icon-white.svg)',
  } /** Riool */,
  R: {
    backgroundColor: 'rgb(124, 91, 174)',
    backgroundImage: 'url(/images/riool-icon-white.svg)',
  } /** Laadpunt */,
  L: {
    backgroundColor: 'rgb(56, 161, 56)',
    backgroundImage: 'url(/images/laadpunt-icon-white.svg)',
  },
} as const

type Props = {
  disciplineVerzamelingen: {
    disciplineCode: DisciplineVerzameling['disciplineCode']
    disciplineLabel: string
    netbeheerderCode: string
    netbeheerderLabel: string
    referentie?: string

    isGrootverbruik?: boolean
    isWachtlijst?: boolean
    isActive?: boolean
    onClick?: () => void
  }[]
}

const DisciplineIcons = ({ disciplineVerzamelingen }: Props) => {
  if (disciplineVerzamelingen.length === 0) {
    return null
  }

  const icons: {
    disciplineCode: DisciplineVerzameling['disciplineCode']
    netbeheerderLabel: string
    tooltip: string[]
    isGrootverbruik?: boolean
    isWachtlijst?: boolean
    isActive?: boolean
    onClick?: () => void
  }[] = []

  for (const disc of disciplineVerzamelingen) {
    const existingIndex = icons.findIndex(
      (d) => d.disciplineCode === disc.disciplineCode,
    )

    if (existingIndex === -1) {
      icons.push({
        disciplineCode: disc.disciplineCode,
        netbeheerderLabel: disc.netbeheerderLabel,
        tooltip: [
          disc.netbeheerderLabel +
            (disc.referentie ? ` (${disc.referentie})` : ''),
        ],
        isGrootverbruik: disc.isGrootverbruik,
        isWachtlijst: disc.isWachtlijst,
        isActive: disc.isActive,
        onClick: disc.onClick,
      })
    } else {
      icons[existingIndex].tooltip.push(
        disc.netbeheerderLabel +
          (disc.referentie ? ` (${disc.referentie})` : ''),
      )
    }
  }

  const sortedIcons = icons.sort((a, b) => {
    const order = Object.keys(backgroundDict)
    return order.indexOf(a.disciplineCode) - order.indexOf(b.disciplineCode)
  })

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 1,
        alignItems: 'center',
        gap: 1,
      }}
    >
      {sortedIcons.map((icon) => (
        <Box
          key={icon.disciplineCode + icon.netbeheerderLabel}
          sx={{ position: 'relative' }}
        >
          <Tooltip title={icon.tooltip.join(', ')}>
            <Box
              key={icon.disciplineCode + icon.netbeheerderLabel}
              sx={{
                ...backgroundDict[icon.disciplineCode],
                width: 30,
                height: 30,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center center',
                borderRadius: '50%',
                backgroundSize: 20,
              }}
            />
          </Tooltip>

          <DisciplineIconFlagBadge
            backgroundColor={
              backgroundDict[icon.disciplineCode].backgroundColor
            }
            isGrootverbruik={icon.isGrootverbruik}
            isWachtlijst={icon.isWachtlijst}
            isActive={icon.isActive}
          />
        </Box>
      ))}
    </Box>
  )
}
